import { Accordion, AccordionSummary, Typography, AccordionDetails, RadioGroup, FormControlLabel, Radio, Switch } from '@mui/material';
import { Box } from '@mui/system';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from 'react';
import { PricingQuoteDetailsDto, PricingQuoteIncludedProductDto, ProductTypeEnum, ScreeningProductPricingDto } from '@inosio/payments-sdk';
import { useSelector, useDispatch } from 'react-redux';
import { buildPricingQuoteDto, selectOnboardingApplicationState } from '../../../pmui/onboarding/sagas/pmonboardingSlice';
import { ProductPreferencesProps } from '../../interfaces/ProductPreferenceInterface';

const ScreeningPreferences = (props: ProductPreferencesProps) => {
  const selectOnboardingState = useSelector(selectOnboardingApplicationState);
  const dispatch = useDispatch();
  const availableScreeningProducts = props.availableProducts as ScreeningProductPricingDto[];
  const pricingQuoteScreeningProductDto = selectOnboardingState.PricingQuoteSetupObj.IncludedProducts.find((prod) => prod.ProductType === ProductTypeEnum.Screening);
  const isScreeningPreferencesDtoValid = props.validator.IsPreferenceObjectComplete(pricingQuoteScreeningProductDto);

  //local states
  const [autoScreen, setAutoScreen] = useState<string>(
    pricingQuoteScreeningProductDto?.AutoScreening === undefined ? '' : pricingQuoteScreeningProductDto?.AutoScreening ? 'autoScreen' : 'chooseMyself',
  );
  const [isScreeningSelected, setIsScreeningSelected] = useState(!!pricingQuoteScreeningProductDto);
  const [selectedScreeningProduct, setSelectedScreeningProduct] = useState<PricingQuoteIncludedProductDto | undefined>(pricingQuoteScreeningProductDto); // credit or credit+ conviction etc
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(!!pricingQuoteScreeningProductDto?.ProductUuid);

  //useffects

  /**
   * This useffect listens for changes in the isScreeningSelected (whether product is removed or added),
   * selectedScreeningProduct (Credit or Credit + Conviction etc) and the autoScreen reporting option state.
   * It then makes changes to the PricingQuoteSetupObj based on these changes.
   */
  useEffect(() => {
    const existingIncludedProductsList = selectOnboardingState.PricingQuoteSetupObj.IncludedProducts;

    let updatedIncludedProductsList = [...existingIncludedProductsList];

    const autOrderScreening = autoScreen === 'autoScreen';
    const selectedProd = {
      ...selectedScreeningProduct,
      ...(autoScreen !== '' && { AutoScreening: autOrderScreening }), // Add AutoScreening only if autoScreen is not empty and is either true or false
    } as PricingQuoteIncludedProductDto;

    //Screening has been toggled on
    if (isScreeningSelected) {
      //if the prerequisite products are not on then turn them on first
      props.preRequisiteProducts.forEach((prereq) => {
        const existingProduct = selectOnboardingState.PricingQuoteSetupObj.IncludedProducts.find((prod) => prod.ProductUuid === prereq.ProductUuid && prod.ProductType === prereq.ProductType);
        if (!existingProduct) {
          const prodToAdd: PricingQuoteIncludedProductDto = {
            ProductUuid: prereq.ProductUuid,
            ProductType: prereq.ProductType,
            ConsumableFee: prereq.ConsumableFee,
            FlatFee: prereq.FlatFee,
            ProductLabel: '', //its fine, we'll be adding labels in the view quote page
          };
          updatedIncludedProductsList.push(prodToAdd);
        }
      });

      let screeningProdToAdd: PricingQuoteIncludedProductDto;
      // if a screening product was selected
      if (!!selectedScreeningProduct) {
        screeningProdToAdd = selectedProd;
      }
      //if not, add a dummy product just to show that the screening toggle was turned on
      else {
        screeningProdToAdd = {
          ProductUuid: '',
          ProductType: ProductTypeEnum.Screening,
          ProductLabel: '',
        };
      }

      // Check if a screening product already exists in the current list by product type
      if (pricingQuoteScreeningProductDto) {
        // Replace the existing screening product with the selected one
        updatedIncludedProductsList = existingIncludedProductsList.map((prod) => (prod.ProductType === ProductTypeEnum.Screening ? screeningProdToAdd : prod));
      } else {
        // else add the new selected screening product
        updatedIncludedProductsList.push(screeningProdToAdd);
      }
    } else {
      // Remove any screening product if screening toggle has been turned off
      updatedIncludedProductsList = existingIncludedProductsList.filter((prod) => prod.ProductType !== ProductTypeEnum.Screening);
    }

    dispatch({
      type: buildPricingQuoteDto.type,
      payload: {
        IncludedProducts: updatedIncludedProductsList,
      } as PricingQuoteDetailsDto,
    });
  }, [selectedScreeningProduct, autoScreen, isScreeningSelected]);

  /**
   * This useffect listens for any changes in our master obj and checks if screening has been added.
   * If it has then it turns on the toggle otherwise turns it off. Useful when turning off/on any other product causes this product to turn on/off
   */
  useEffect(() => {
    if (!!pricingQuoteScreeningProductDto) {
      setIsScreeningSelected(true);
    } else {
      setIsScreeningSelected(false);
    }
  }, [selectOnboardingState.PricingQuoteSetupObj]);

  /**
   * Expands the accordion if we are in validation mode for better ux
   */
  useEffect(() => {
    if (props.inValidationMode) {
      setIsAccordionExpanded(true);
    }
  }, [props.inValidationMode]);

  return (
    <Accordion
      sx={{ marginTop: 2 }}
      elevation={0}
      expanded={isAccordionExpanded}
      onChange={(event, expanded) => {
        setIsAccordionExpanded(expanded);
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ marginTop: 2, boxShadow: 'none', '& .MuiPaper-root': { boxShadow: 'none' } }}>
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
          <Switch
            checked={isScreeningSelected}
            onChange={(e) => {
              if (props.isCustomMode) {
                setIsScreeningSelected(e.target.checked);
                if (!e.target.checked) {
                  setSelectedScreeningProduct(undefined);
                  setAutoScreen('');
                }
              }
            }}
          />
          <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 600, fontSize: 'large' }}>Screening</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          <Typography
            sx={{ fontFamily: 'Work Sans !important', marginBottom: 1, color: props.inValidationMode && !isScreeningPreferencesDtoValid && !selectedScreeningProduct?.ProductUuid ? 'red' : 'black' }}
          >
            Report Options:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2, mt: 2 }}>
            {availableScreeningProducts?.map((sp) => {
              return (
                <FormControlLabel
                  key={sp.ProductUuid}
                  value={sp.ProductUuid}
                  control={
                    <Radio
                      checked={selectedScreeningProduct?.ProductUuid === sp.ProductUuid}
                      onChange={() => {
                        setIsScreeningSelected(true);
                        const screeningObj: PricingQuoteIncludedProductDto = {
                          ProductUuid: sp.ProductUuid,
                          ProductType: ProductTypeEnum.Screening,
                          ConsumableFee: sp.Cost,
                          ProductLabel: sp.Label,
                        };
                        setSelectedScreeningProduct(screeningObj);
                      }}
                    />
                  }
                  label={`${sp.Label || ''} $${(sp.Cost || 0) / 100}`}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontFamily: 'Work Sans !important',
                    },
                  }}
                />
              );
            })}
          </Box>
          <Typography
            sx={{ fontFamily: 'Work Sans !important', marginTop: 2, marginBottom: 1, color: props.inValidationMode && !isScreeningPreferencesDtoValid && autoScreen === '' ? 'red' : 'black' }}
          >
            Ordering Options:
          </Typography>
          <RadioGroup
            name="orderingOptions"
            value={autoScreen}
            onChange={(e) => {
              setIsScreeningSelected(true);
              setAutoScreen(e.target.value);
            }}
            sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2, mt: 2 }}
          >
            <FormControlLabel
              value="chooseMyself"
              control={<Radio />}
              label="I will manually select each application to order a screening report for."
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontFamily: 'Work Sans !important',
                },
              }}
            />
            <FormControlLabel
              value="autoScreen"
              control={<Radio />}
              label="Automatically order a screening report for every application submitted."
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontFamily: 'Work Sans !important',
                },
              }}
            />
          </RadioGroup>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default ScreeningPreferences;
